// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import XngZone from "@flip/App/Components/Common/XngZone";
import {
    CopyRightWrapper,
    FooterBottomSection,
    FooterContainerWrapper,
    FooterContentWrapper,
    FooterSiteMapLinksWrapper,
    FooterUpperContentWrapper,
    GeneralLinksWrapper,
    LeftSectionWrapper,
    RightSectionWrapper,
} from "@flip/Overrides/Widgets/Footer/FooterContainer.style";
import { checkoutPages } from "@flip/XDL/constants";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const { isCheckoutPage } = useAppSelector(state => ({
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
    }));

    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    const copyrightMessage = () => {
        const message = siteMessage("Flip_Footer_Copyright") as string;
        return message.replace("{0}", String(new Date().getFullYear()));
    };
    return (
        <FooterContainerWrapper {...{ isCheckoutPage }}>
            {!isCheckoutPage && (
                <FooterUpperContentWrapper>
                    <LeftSectionWrapper>
                        <FooterContentWrapper>
                            <LeftSectionWrapper>
                                <StyledWrapper className="logo-wrapper">
                                    <XngZone hint="Zone for company Logo" zoneName="Logo" contentId={id} />
                                </StyledWrapper>
                                <StyledWrapper className="address-wrapper">
                                    <XngZone hint="Zone for Address" zoneName="FooterAddress" contentId={id} />
                                    <StyledWrapper className="phone-email-wrapper">
                                        <XngZone
                                            hint="Zone for Phone And Hours"
                                            zoneName="FooterPhone"
                                            contentId={id}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                                <StyledWrapper className="footer-icons-subscription-wrapper">
                                    <Hidden below="lg" className="social-links-wrapper">
                                        <StyledWrapper className="footer-icons-wrapper">
                                            <StyledWrapper className="awards-logos-wrapper">
                                                <XngZone
                                                    hint="Zone for awards logos"
                                                    zoneName="awardsLogos"
                                                    contentId={id}
                                                />
                                            </StyledWrapper>
                                            <XngZone
                                                hint="Zone for Social Links"
                                                zoneName="SocialLinks"
                                                contentId={id}
                                            />
                                        </StyledWrapper>
                                    </Hidden>
                                    <Hidden below="md" className="email-subsctiption-wrapper">
                                        <XngZone
                                            hint="Zone for email subsctiption"
                                            zoneName="FooterEmailSubscription"
                                            contentId={id}
                                        />
                                    </Hidden>
                                </StyledWrapper>
                            </LeftSectionWrapper>
                            <StyledWrapper className="links-logos-wrapper">
                                <GeneralLinksWrapper>
                                    <Zone zoneName="LinkList1" contentId={id} />
                                </GeneralLinksWrapper>
                                <Hidden above="md">
                                    <StyledWrapper className="footer-icons-wrapper">
                                        <StyledWrapper className="awards-logos-wrapper">
                                            <XngZone
                                                hint="Zone for awards logos"
                                                zoneName="awardsLogos"
                                                contentId={id}
                                            />
                                        </StyledWrapper>
                                        <XngZone hint="Zone for Social Links" zoneName="SocialLinks" contentId={id} />
                                    </StyledWrapper>
                                </Hidden>
                            </StyledWrapper>
                        </FooterContentWrapper>
                    </LeftSectionWrapper>
                    <RightSectionWrapper>
                        <Hidden above="sm" className="email-subsctiption-wrapper">
                            <XngZone
                                hint="Zone for email subsctiption"
                                zoneName="FooterEmailSubscription"
                                contentId={id}
                            />
                        </Hidden>
                        <XngZone hint="Zone for Contact" zoneName="contactusDesktopAndTablet" contentId={id} />
                    </RightSectionWrapper>
                </FooterUpperContentWrapper>
            )}
            <FooterBottomSection {...{ isCheckoutPage }}>
                <CopyRightWrapper {...{ isCheckoutPage }}>{copyrightMessage()}</CopyRightWrapper>
                <FooterSiteMapLinksWrapper>
                    <XngZone zoneName="CopyrightLinks" contentId={id} />
                </FooterSiteMapLinksWrapper>
            </FooterBottomSection>
        </FooterContainerWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
